import { useCallback, useEffect, useState } from "react";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { AddButton } from "../design/AddLink";
import { Tabs } from "../design/Tabs";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import type { Tone } from "../design/tone";
import { useSession } from "../session/SessionProvider";
import { when } from "../system/format";

// ---------------------------------------------------------------------------
// Sweep §4 / 0234–0236, S121–S124. API AND WEBHOOKS, under Organisation.
//
// Keys let the agency's own systems READ its events, workers and timesheets.
// Webhooks tell an address the agency registered when something happens; a
// person presses "Send waiting deliveries" because nothing here runs on a
// schedule. Every secret is shown once, in the response that made it, and this
// screen keeps it only until the person dismisses it.
// ---------------------------------------------------------------------------

type Vocab = { id: string; label: string; description: string };

type Key = {
  id: string;
  name: string;
  scopes: string[];
  key_prefix: string;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  created_by_you: boolean;
};

type Endpoint = { id: string; url: string; event_kinds: string[]; created_at: string; revoked_at: string | null };

type Delivery = {
  id: string;
  url: string;
  kind_label: string;
  state_label: string;
  state_tone: Tone;
  attempts: number;
  last_status: number | null;
  last_error: string | null;
  created_at: string;
  last_attempt_at: string | null;
};

type Keys = { keys: Key[]; scopes: Vocab[] };
type Hooks = { endpoints: Endpoint[]; deliveries: Delivery[]; kinds: Vocab[] };
type Failure = { error?: { message?: string } };

const OFFLINE = "We could not reach Verbunk. Check your connection and try again.";

type Say = ReturnType<typeof useT>;

/** One request, answered as the body or a sentence saying why not, in the reader's language. */
async function attempt<T>(spec: Parameters<typeof send>[0], { t, refusal }: Say): Promise<{ body: T | null; error: string | null }> {
  try {
    const res = await send<T & Failure>(spec);
    if (res.status === 200 || res.status === 201) return { body: res.body, error: null };
    return { body: null, error: refusal(res.body.error) ?? t("That did not work.") };
  } catch {
    return { body: null, error: t(OFFLINE) };
  }
}

export function ApiAndWebhooks() {
  const { apiBase } = useSession();
  const say = useT();
  const { t } = say;
  const [keys, setKeys] = useState<Keys | null>(null);
  const [hooks, setHooks] = useState<Hooks | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [k, h] = await Promise.all([attempt<Keys>(console_.apiKeys(apiBase), say), attempt<Hooks>(console_.webhooks(apiBase), say)]);
    setKeys(k.body && Array.isArray(k.body.keys) ? k.body : null);
    setHooks(h.body && Array.isArray(h.body.endpoints) ? h.body : null);
    setFailed(k.error ?? h.error);
  }, [apiBase, say]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="stack">
      <div className="panel-head">
        <h1>{t("API and webhooks")}</h1>
      </div>
      {failed ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not available")} />
          <span>{failed}</span>
        </div>
      ) : null}
      <Tabs
        label={t("API and webhooks")}
        tabs={[
          { id: "api-keys", label: t("API keys"), content: keys ? <KeysPanel data={keys} onChanged={load} /> : null },
          { id: "api-hooks", label: t("Webhooks"), content: hooks ? <WebhooksPanel data={hooks} onChanged={load} /> : null },
        ]}
      />
    </div>
  );
}

/** A secret, shown once. Dismissing it is the only way to make it go. */
function OnceSecret({ label, secret, onDone }: { label: string; secret: string; onDone: () => void }) {
  const { t } = useT();
  return (
    <div className="form-message stack" role="status">
      <StatusChip tone="attention" label={t("Copy it now")} />
      <div className="field">
        <label htmlFor="once-secret">{label}</label>
        <input id="once-secret" readOnly value={secret} onFocus={(e) => e.target.select()} />
        <p className="field-hint">{t("This is the only time it is shown. Verbunk keeps no copy it can show you again.")}</p>
      </div>
      <button type="button" className="btn btn-quiet" onClick={onDone}>
        {t("I have copied it")}
      </button>
    </div>
  );
}

function Checkboxes({
  name,
  options,
  chosen,
  onChange,
}: {
  name: string;
  options: Vocab[];
  chosen: string[];
  onChange: (next: string[]) => void;
}) {
  const { locale } = useT();
  return (
    <div className="field" role="group" aria-label={name}>
      <strong>{name}</strong>
      {options.map((o) => (
        <label key={o.id} className="check">
          <input
            type="checkbox"
            checked={chosen.includes(o.id)}
            onChange={(e) => onChange(e.target.checked ? [...chosen, o.id] : chosen.filter((c) => c !== o.id))}
          />
          <span>
            {translateLabel(locale, o.label)} <span className="muted">— {translateLabel(locale, o.description)}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

function KeysPanel({ data, onChanged }: { data: Keys; onChanged: () => Promise<void> }) {
  const { apiBase } = useSession();
  const say = useT();
  const { locale, t } = say;
  const [making, setMaking] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Revoking cannot be undone, so the first click only asks (Requirements.tsx §5: in-page, never window.confirm).
  const [confirming, setConfirming] = useState<string | null>(null);
  const label = (id: string) => translateLabel(locale, data.scopes.find((s) => s.id === id)?.label ?? id);

  async function create() {
    setBusy(true);
    setNotice(null);
    const r = await attempt<{ key: { secret: string } }>(console_.createApiKey(apiBase, name, scopes), say);
    setBusy(false);
    if (!r.body) return setNotice(r.error);
    setSecret(r.body.key.secret);
    setMaking(false);
    setName("");
    setScopes([]);
    await onChanged();
  }

  async function revoke(id: string) {
    setBusy(true);
    setNotice(null);
    const r = await attempt(console_.revokeApiKey(apiBase, id), say);
    setBusy(false);
    if (r.error) return setNotice(r.error);
    await onChanged();
  }

  return (
    <section className="panel stack">
      <div className="panel-head">
        <h2>{t("API keys")}</h2>
        <AddButton label={t("Make a key")} expanded={making} onClick={() => setMaking(!making)} />
      </div>
      <p className="muted">
        {t("A key lets your payroll, HR or reporting system read from Verbunk. Send it as")} <code>Authorization: Bearer</code>.{" "}
        {t("Keys only read, and only what their scopes name. A key stops working when it is revoked, or when the person who made it can no longer manage keys.")}
      </p>
      {secret ? <OnceSecret label={t("Your new key")} secret={secret} onDone={() => setSecret(null)} /> : null}
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not saved")} />
          <span>{notice}</span>
        </div>
      ) : null}
      {data.keys.length === 0 ? <p className="muted">{t("No keys yet.")}</p> : null}
      {data.keys.length > 0 ? (
        <ul className="plain-list">
          {data.keys.map((k) => (
            <li key={k.id} className="stack">
              <div className="panel-head">
                <h3>{k.name}</h3>
                <StatusChip tone={k.revoked_at ? "neutral" : "live"} label={k.revoked_at ? t("Revoked") : t("Live")} />
              </div>
              <p>
                <code>{k.key_prefix}…</code> · {k.scopes.map(label).join(", ")}
              </p>
              <p className="muted">
                {k.created_by_you
                  ? t("Made {when} by you.", { when: when(k.created_at) })
                  : t("Made {when}.", { when: when(k.created_at) })}{" "}
                {k.last_used_at ? t("Last used {when}.", { when: when(k.last_used_at) }) : t("Never used.")}
                {k.revoked_at ? ` ${t("Revoked {when}.", { when: when(k.revoked_at) })}` : ""}
              </p>
              {k.revoked_at ? null : confirming === k.id ? (
                <div className="row">
                  <span>{t("Revoke this key? It stops working at once for anything that uses it, and it cannot be turned back on.")}</span>
                  <button type="button" className="btn" disabled={busy} onClick={() => void revoke(k.id)}>
                    {busy ? t("Revoking…") : t("Yes, revoke it")}
                  </button>
                  <button type="button" className="btn btn-quiet" onClick={() => setConfirming(null)}>
                    {t("Keep it")}
                  </button>
                </div>
              ) : (
                <div className="row">
                  <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setConfirming(k.id)}>
                    {t("Revoke")}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {making ? (
        <div className="stack">
          <div className="field">
            <label htmlFor="api-key-name">{t("Name")}</label>
            <input id="api-key-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
            <p className="field-hint">{t("Say what will use it, for example “Payroll export”.")}</p>
          </div>
          <Checkboxes name={t("What it may read")} options={data.scopes} chosen={scopes} onChange={setScopes} />
          <div className="row">
            <button type="button" className="btn" disabled={busy || name.trim() === "" || scopes.length === 0} onClick={() => void create()}>
              {t("Make the key")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setMaking(false)}>
              {t("Cancel")}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function WebhooksPanel({ data, onChanged }: { data: Hooks; onChanged: () => Promise<void> }) {
  const { apiBase } = useSession();
  const say = useT();
  const { locale, t } = say;
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [kinds, setKinds] = useState<string[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // A busy integration queues dozens; the newest few are the ones anybody reads.
  const [allDeliveries, setAllDeliveries] = useState(false);
  // Removing drops whatever is still waiting for the address, so the first click only asks.
  const [confirming, setConfirming] = useState<string | null>(null);
  const label = (id: string) => translateLabel(locale, data.kinds.find((k) => k.id === id)?.label ?? id);
  const waiting = data.deliveries.filter((d) => d.state_tone === "attention").length;

  async function act<T>(spec: Parameters<typeof send>[0], then?: (body: T) => void) {
    setBusy(true);
    setNotice(null);
    const r = await attempt<T>(spec, say);
    setBusy(false);
    if (!r.body) return setNotice(r.error);
    then?.(r.body);
    await onChanged();
  }

  return (
    <section className="panel stack">
      <div className="panel-head">
        <h2>{t("Webhooks")}</h2>
        {waiting > 0 ? <StatusChip tone="attention" label={t("{n} waiting", { n: waiting })} /> : null}
        <AddButton label={t("Add an address")} expanded={adding} onClick={() => setAdding(!adding)} />
      </div>
      <p className="muted">
        {t("Verbunk posts to your address when something happens, signed with the secret shown when you add it. Check this header:")}{" "}
        <code>Verbunk-Webhook-Signature</code>{" "}
        {t("— an HMAC-SHA256 of the timestamp header, a full stop and the body. Deliveries wait until somebody sends them from here; a failed one is tried again later, six times in all.")}
      </p>
      {secret ? <OnceSecret label={t("Your signing secret")} secret={secret} onDone={() => setSecret(null)} /> : null}
      {notice ? (
        <div className="form-message" role="alert">
          <StatusChip tone="attention" label={t("Not done")} />
          <span>{notice}</span>
        </div>
      ) : null}
      {data.endpoints.length === 0 ? <p className="muted">{t("No addresses yet.")}</p> : null}
      {data.endpoints.length > 0 ? (
        <ul className="plain-list">
          {data.endpoints.map((e) => (
            <li key={e.id} className="stack">
              <div className="panel-head">
                <h3>
                  <code>{e.url}</code>
                </h3>
                <StatusChip tone={e.revoked_at ? "neutral" : "live"} label={e.revoked_at ? t("Removed") : t("Live")} />
              </div>
              <p className="muted">
                {t("{kinds}. Added {when}.", { kinds: e.event_kinds.map(label).join(", "), when: when(e.created_at) })}
              </p>
              {e.revoked_at ? null : confirming === e.id ? (
                <div className="row">
                  <span>{t("Remove this address? Verbunk stops sending to it at once, and anything still waiting for it will never be sent.")}</span>
                  <button type="button" className="btn" disabled={busy} onClick={() => void act(console_.revokeWebhook(apiBase, e.id))}>
                    {busy ? t("Removing…") : t("Yes, remove it")}
                  </button>
                  <button type="button" className="btn btn-quiet" onClick={() => setConfirming(null)}>
                    {t("Keep it")}
                  </button>
                </div>
              ) : (
                <div className="row">
                  <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setConfirming(e.id)}>
                    {t("Remove")}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {adding ? (
      <div className="stack">
      <div className="field">
        <label htmlFor="webhook-url">{t("Address")}</label>
        <input id="webhook-url" type="url" inputMode="url" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
        <p className="field-hint">{t("It must start with https:// and be reachable on the public internet.")}</p>
      </div>
      <Checkboxes name={t("Tell it when")} options={data.kinds} chosen={kinds} onChange={setKinds} />
      <div className="row">
        <button
          type="button"
          className="btn"
          disabled={busy || url.trim() === "" || kinds.length === 0}
          onClick={() =>
            void act<{ endpoint: { secret: string } }>(console_.registerWebhook(apiBase, url.trim(), kinds), (b) => {
              setSecret(b.endpoint.secret);
              setUrl("");
              setKinds([]);
              setAdding(false);
            })
          }
        >
          {t("Add the address")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={() => setAdding(false)}>
          {t("Cancel")}
        </button>
      </div>
      </div>
      ) : null}
      <h3>{t("Deliveries")}</h3>
      <div className="row">
        <button type="button" className="btn" disabled={busy} onClick={() => void act(console_.drainWebhooks(apiBase))}>
          {t("Send waiting deliveries")}
        </button>
      </div>
      {data.deliveries.length === 0 ? <p className="muted">{t("Nothing has been sent yet.")}</p> : null}
      {data.deliveries.length > 0 ? (
        <ul className="plain-list">
          {(allDeliveries ? data.deliveries : data.deliveries.slice(0, 5)).map((d) => (
            <li key={d.id}>
              <div className="panel-head">
                <span>
                  {translateLabel(locale, d.kind_label)} · <code>{d.url}</code>
                </span>
                <StatusChip tone={d.state_tone} label={translateLabel(locale, d.state_label)} />
              </div>
              <p className="muted">
                {t("Queued {when}.", { when: when(d.created_at) })}{" "}
                {d.attempts === 0
                  ? t("Not tried yet.")
                  : t("Tried {n} time(s), last {when}.", { n: d.attempts, when: when(d.last_attempt_at!) })}
                {d.last_error ? ` ${translateLabel(locale, d.last_error)}` : ""}
              </p>
            </li>
          ))}
        </ul>
      ) : null}
      {data.deliveries.length > 5 ? (
        <div className="row">
          <button type="button" className="btn btn-quiet" onClick={() => setAllDeliveries(!allDeliveries)}>
            {allDeliveries ? t("Show fewer") : t("Show all ({n})", { n: data.deliveries.length })}
          </button>
        </div>
      ) : null}
    </section>
  );
}
